/** Home's first-run list: at most five lines, each a few words, a state and the way to do it
 * (ADR-0212 decision 2: "the first-run empty state is a short checklist, not paragraphs").
 *
 * It is `FirstRunJourney`'s plan drawn as the shared `Checklist`. Nothing here is a stored flag and
 * nothing is ticked by a person: every state comes from `useFirstRunJourney`, which derives it from
 * the companies, sites, periods, figures and reports on each render, so a step that stops being true
 * un-ticks itself. A step the plan could not read is **not done and not offered** (it is a "to do"
 * with no link), because a dropped request must not become "you have not stated your financials".
 *
 * **Who can is told by the absence of a control** (design rule 6): stating financials, companies,
 * sites and periods are Admin routes; activity is `MAY_EDIT`. A role that may not sees the line with
 * no link. The reasons each step exists, and the regimes the first step settles, are on Readiness
 * (`FirstRunChecklist`) and behind the info control - nothing was deleted, only moved off Home.
 */

import { Link } from "@tanstack/react-router";

import { useMe } from "../api/auth";
import { ReadFailed } from "../components/Loaded";
import { Checklist, EmptyAction } from "../components/ui";
import type { ChecklistItem } from "../components/ui";
import type { ChecklistKey, JourneyPlan } from "./firstRunJourney";
import { keepSearch } from "./search";

const MAY_RECORD: readonly string[] = ["admin", "approver", "contributor"];

const LABEL: Record<ChecklistKey, string> = {
  financials: "Company facts",
  regimes: "Regimes",
  sites: "Sites, period",
  activity: "First bill",
  outstanding: "Filings owed",
};

export function HomeChecklist({ plan }: { plan: NonNullable<JourneyPlan> }) {
  const { data: me } = useMe();
  const role = me?.role;

  if (plan.kind === "unread") {
    return (
      <div data-testid="first-run-checklist-unread">
        <ReadFailed of="The data behind this checklist" error={plan.error} onRetry={plan.retry} />
      </div>
    );
  }

  // Where a step is done, and the verb for it. A role that may not do a step is shown no link.
  const target = (key: ChecklistKey): { to: string; label: string; tab?: string } | null => {
    if (key === "outstanding") {
      // Reading the list needs no role: the reports are readable by everyone in the workspace.
      return plan.outstanding[0] ? { to: "/reports", label: "Open the filing" } : null;
    }
    // Unknown role (the session read is in flight): no control on a guess.
    if (role === undefined) return null;
    if (!(key === "activity" ? MAY_RECORD.includes(role) : role === "admin")) return null;
    if (key === "activity") return { to: "/upload", label: "Upload a bill" };
    if (key === "financials" && plan.entityCount === 0) {
      return { to: "/facilities", label: "Add a company", tab: "companies" };
    }
    if (key === "sites") return { to: "/facilities", label: "Add a site" };
    return { to: "/financials", label: key === "regimes" ? "Check regimes" : "State facts" };
  };

  const first = plan.outstanding[0];
  const searchFor =
    (key: ChecklistKey, tab?: string) =>
    (prev: Record<string, unknown>) => ({
      ...keepSearch(prev),
      ...(tab ? { tab } : {}),
      ...(key === "outstanding" && first ? { filing: first.obligation_id } : {}),
    });

  const items: ChecklistItem[] = plan.steps.map((step) => {
    const where = step.state === "done" ? null : target(step.key);
    return {
      id: step.key,
      label: LABEL[step.key],
      // Every step that is still to do links to where it is done (KD, 2026-11-02); the browser gate
      // reads `first-run-checklist-action` as the one control, so only the step to do now carries it.
      labelLink: where ? (
        <Link
          to={where.to}
          search={searchFor(step.key, where.tab)}
          data-testid="first-run-checklist-step-link"
        >
          {LABEL[step.key]}
        </Link>
      ) : undefined,
      state: step.state === "done" ? "done" : "todo",
      phase: step.state,
      action:
        where && step.state === "next" ? (
          <EmptyAction
            label={where.label}
            allowed
            to={where.to}
            search={searchFor(step.key, where.tab)}
            plus={false}
            testId="first-run-checklist-action"
          />
        ) : undefined,
    };
  });
  return <Checklist title="First filing" items={items} testId="first-run-checklist" />;
}
